/**
 * Helpers shared by the Billing and Subscription pages for redirect-based
 * gateway checkouts (PayPal): building return URLs, reading the query
 * string PayPal sends the buyer back with, and describing plan changes.
 */

const CHECKOUT_PARAM_KEYS = [
  "checkout",
  "intent",
  "subscription",
  // Added by PayPal on return.
  "token",
  "PayerID",
  "subscription_id",
  "ba_token",
];

/** Gateways the frontend can complete by redirecting the buyer. */
export const getRedirectGateways = (gateways = [], { subscriptions = false } = {}) =>
  (Array.isArray(gateways) ? gateways : []).filter(
    (gateway) =>
      gateway?.checkout_mode === "redirect" &&
      (!subscriptions || gateway?.supports_subscriptions),
  );

/** Absolute URL on this frontend that PayPal sends the buyer back to. */
export const buildCheckoutReturnUrl = (path, origin = window.location.origin) =>
  `${origin.replace(/\/$/, "")}${path.startsWith("/") ? path : `/${path}`}`;

/**
 * Parse the query string after a gateway redirect.
 * @returns {{ status: "returned"|"cancelled", gateway: string|null, intentId: string|null, subscriptionId: string|null } | null}
 */
export const readCheckoutReturn = (searchParams) => {
  const checkout = searchParams?.get?.("checkout");
  if (!checkout) return null;
  const intentId = searchParams.get("intent");
  const subscriptionId = searchParams.get("subscription");
  if (!intentId && !subscriptionId) return null;
  return {
    status: checkout === "cancelled" ? "cancelled" : "returned",
    gateway: checkout === "cancelled" ? null : checkout,
    intentId,
    subscriptionId,
  };
};

/** A copy of ``searchParams`` without checkout/return parameters. */
export const withoutCheckoutParams = (searchParams) => {
  const next = new URLSearchParams(searchParams);
  CHECKOUT_PARAM_KEYS.forEach((key) => next.delete(key));
  return next;
};

/** Prefer the backend's ``{ error }`` message over the generic client one. */
export const checkoutErrorMessage = (err, fallback = "Something went wrong. Please try again.") =>
  err?.data?.error || err?.data?.detail || err?.message || fallback;

export const tierPriceFor = (tier, interval) => {
  const raw = interval === "year" ? tier?.price_yearly : tier?.price_monthly;
  const amount = Number(raw);
  return Number.isFinite(amount) && amount > 0 ? amount : null;
};

export const formatMoney = (amount, currency = "USD") => {
  const value = Number(amount);
  if (!Number.isFinite(value)) return "";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(value);
  } catch {
    return `$${value.toFixed(2)}`;
  }
};

/**
 * Mirror of the backend rule for when a paid plan change takes effect:
 * upgrades (and first purchases) start now; downgrades, interval switches
 * and re-subscribing after a cancel start when the paid period ends.
 */
export const describePlanChange = ({ tier, interval, subscription, now = Date.now() }) => {
  const billing = subscription?.billing;
  const periodEnd = billing?.current_period_end ? new Date(billing.current_period_end) : null;
  const hasPaidPeriod = Boolean(
    billing && periodEnd && periodEnd.getTime() > now + 5 * 60 * 1000 &&
      ["active", "past_due", "suspended", "cancelled"].includes(billing.status),
  );
  if (!hasPaidPeriod) {
    return { startsAt: null, replacesPaidPlan: false };
  }
  const isUpgrade = (tier?.sort_order ?? 0) > (billing.tier_sort_order ?? 0);
  if (isUpgrade) {
    return { startsAt: null, replacesPaidPlan: true };
  }
  const samePlan = subscription?.tier === tier?.code && billing.interval === interval;
  return {
    startsAt: periodEnd,
    replacesPaidPlan: false,
    alreadySubscribed: samePlan && !subscription?.cancel_at_period_end,
  };
};
